"use client";

import styles from "./TextInput.module.scss";

import React, { useId, useLayoutEffect, useRef, useState } from "react";
import FieldError, { fieldErrorBorder } from "../FieldError";
import {
  formatPhoneNumber,
  getPhoneDigits,
} from "@/helpers/phone";

// Only move the caret while someone is typing in the field. Safari treats
// setSelectionRange on an unfocused input (e.g. after autofill) as a focus
// change and jumps to it.
const placeCaret = (input: HTMLInputElement | null, caret: number) => {
  if (input && document.activeElement === input) {
    input.setSelectionRange(caret, caret);
  }
};

const countDigits = (value: string) => value.replace(/\D/g, "").length;

// Caret index just after the nth digit of a formatted number.
const caretAfterDigit = (formatted: string, digitCount: number) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]) && ++seen === digitCount) return i + 1;
  }
  return formatted.length;
};

const PhoneInput = ({
  label = "",
  name = "",
  value = "",
  placeholder = "",
  background = "bg-transparent",
  required = false,
  type = "tel",
  error = "",
  ...props
}) => {
  const errorId = useId();
  const [phoneNumber, setPhoneNumber] = useState(formatPhoneNumber(value) || "");
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingCaret = useRef<number | null>(null);

  // Reformatting replaces the whole value, which would otherwise throw the
  // caret to the end and send the next keystrokes there when someone is
  // fixing a digit in the middle of the number.
  useLayoutEffect(() => {
    if (pendingCaret.current === null) return;
    placeCaret(inputRef.current, pendingCaret.current);
    pendingCaret.current = null;
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const rawDigits = input.value.replace(/\D/g, "");
    const droppedCountryCode = rawDigits.length > 10 && rawDigits.startsWith("1");

    // Typing into a complete number would push a digit off the end; ignore
    // the keystroke instead (pastes of longer strings still get trimmed).
    if (
      getPhoneDigits(phoneNumber).length === 10 &&
      rawDigits.length - (droppedCountryCode ? 1 : 0) > 10 &&
      rawDigits.length === countDigits(phoneNumber) + 1
    ) {
      // React restores the controlled value without re-rendering, so put
      // the caret back once it has.
      const caret = Math.max((input.selectionStart ?? 1) - 1, 0);
      requestAnimationFrame(() => placeCaret(input, caret));
      return;
    }

    const formattedNumber = formatPhoneNumber(input.value) || "";
    const digitsBeforeCaret =
      countDigits(input.value.slice(0, input.selectionStart ?? input.value.length)) -
      (droppedCountryCode ? 1 : 0);
    const caret = caretAfterDigit(formattedNumber, digitsBeforeCaret);
    if (formattedNumber === phoneNumber) {
      // e.g. backspacing over ")" or "-": nothing re-renders, so place the
      // caret after React restores the value.
      requestAnimationFrame(() => placeCaret(input, caret));
    } else {
      pendingCaret.current = caret;
      setPhoneNumber(formattedNumber);
    }
  };

  return (
    <div className={styles["text-input"]} {...props}>
      <label htmlFor={name} className="sr-only">
        {label}
      </label>
      <input
        type={type}
        name={name}
        id={name}
        ref={inputRef}
        inputMode="tel"
        autoComplete="tel-national"
        placeholder={placeholder || label}
        className={`rounded-button border border-border-color color-border-color w-full h-[70px] flex justify-between items-center px-10 leading-none text-base sm:text-lg ${background.includes("bg-white") ? "text-dark-blue" : ""} ${background} ${error ? fieldErrorBorder : ""}`}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        value={phoneNumber}
        onChange={handleChange}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
};

export default PhoneInput;
